<template>
  <div :class="containerClass" :style="backgroundImageStyle">
    <div class="headings-container">
      <prismic-rich-text
        v-if="heading"
        :style="{ fontSize: 14, color: textColor }"
        :field="heading"
      ></prismic-rich-text>
      <prismic-rich-text
        v-if="subheading"
        :style="{ color: textColor }"
        :field="subheading"
      ></prismic-rich-text>
    </div>
    <div class="grid-layout" :class="{ 'grid-layout-carousel': mobileCarousel }">
      <div v-for="(item, idx) in slice.items" :key="idx" class="item-container">
        <picture v-if="checkForFieldExistance(item.item_image, 'url')">
          <source
            v-for="{ resolution, size } in imageSizes"
            :key="resolution"
            :media="`(max-width: ${resolution}px)`"
            :srcset="`${item.item_image.url}&w=${size}&fmt=webp`"
          />
          <img
            height="auto"
            width="100%"
            :class="{ 'big-img': slice.items.length <= 3 }"
            :src="item.item_image.url"
            :alt="item.item_image.alt"
            loading="lazy"
          />
        </picture>
        <prismic-rich-text
          v-if="item.item_info_title"
          :style="{ color: item.item_text_color }"
          :field="item.item_info_title"
        ></prismic-rich-text>
        <prismic-rich-text
          v-if="item.item_info_description"
          :style="{ color: item.item_text_color }"
          class="item-description"
          :field="item.item_info_description"
        ></prismic-rich-text>
        <Button
          v-if="
            checkForFieldExistance(item.item_button[0]) &&
            checkForFieldExistance(item.item_button_link[0])
          "
          :class="{ 'buttons-aligned': buttonsAligned }"
          v-bind="buttonProps(item.button_style, item.primary_color, item.text_color)"
          :blank="!item.is_link_internal"
          :to="
            item.is_link_internal
              ? $trailingSlash(localePath(item.item_button_link[0].text))
              : item.item_button_link[0].text
          "
        >
          {{ item.item_button[0].text }}
        </Button>
      </div>
    </div>
    <Button
      v-if="buttonText && buttonLink"
      class="my-4"
      :blank="!isLinkInternal"
      v-bind="buttonProps(buttonStyle, buttonPrimaryColor, buttonTextColor)"
      :to="isLinkInternal ? $trailingSlash(localePath(buttonLink)) : buttonLink"
    >
      {{ buttonText }}
    </Button>
  </div>
</template>

<script>
import Button from 'UI/Button';
import { mapGetters } from 'vuex';

export default {
  components: {
    Button
  },
  props: {
    slice: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      heading: '',
      subheading: '',
      useExpColor: true,
      customColor: '#3f80f1',
      backgroundImageUrl: '',
      fullWidth: false,
      buttonsAligned: false,
      buttonText: '',
      buttonLink: '',
      buttonPrimaryColor: '',
      buttonTextColor: '',
      buttonStyle: '',
      isLinkInternal: true,
      mobileCarousel: false,
      imageSizes: [
        { resolution: 360, size: 330 },
        { resolution: 375, size: 345 },
        { resolution: 414, size: 384 },
        { resolution: 1920, size: 550 }
      ]
    };
  },
  computed: {
    ...mapGetters('experience', ['getExperienceColor']),
    textColor() {
      return this.useExpColor ? this.getExperienceColor : this.customColor;
    },
    backgroundImageStyle() {
      return this.backgroundImageUrl
        ? `padding: 20px 30px; 
          border-radius: 5px; 
          background-image: url(${this.backgroundImageUrl});
          background-size: cover; `
        : '';
    },
    containerClass() {
      const resolveContainer = this.mobileCarousel ? 'container-md' : 'container';
      return `component-container ${!this.fullWidth ? resolveContainer : ''}`;
    }
  },
  created() {
    const { primary } = this.slice;
    this.heading = primary.heading;
    this.subheading = primary.subheading;
    this.useExpColor = primary.use_experience_color_heading;
    this.customColor = primary.heading_color;
    this.backgroundImageUrl = primary.background_image.url;
    this.fullWidth = primary.full_width;
    this.buttonsAligned = primary.buttons_aligned;
    this.buttonText = primary.button_text;
    this.buttonLink = primary.button_link[0]?.text ? primary.button_link[0].text : '';
    this.buttonPrimaryColor = primary.button_primary_color;
    this.buttonTextColor = primary.button_text_color;
    this.buttonStyle = primary.button_style;
    this.isLinkInternal = primary.isLinkInternal;
    this.mobileCarousel = primary.mobile_carousel;
  },
  methods: {
    checkForFieldExistance(exp, field = 'text') {
      return exp?.[field];
    },
    buttonProps(buttonStyle, primaryColor, textColor) {
      const props =
        buttonStyle == 'outline'
          ? {
              shape: 'outline',
              color: 'transparent',
              hover: 'false',
              fontColor: primaryColor || this.getExperienceColor
            }
          : buttonStyle == 'custom'
          ? {
              gradient: primaryColor || this.getExperienceColor,
              withGradient: true,
              fontColor: textColor || '#FFF'
            }
          : buttonStyle == 'experience-outline'
          ? {
              shape: 'outline-transparent outline'
            }
          : {};
      return props;
    }
  }
};
</script>

<style lang="scss" scoped>
.component-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  .headings-container {
    margin: 1rem 0;
    text-align: center;
    width: 100%;
    max-width: 50rem;
    @include min-md {
      margin: 3rem 0;
    }
  }
  .grid-layout {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
    grid-gap: 2rem;
    .item-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      max-width: 30%;
      .buttons-aligned {
        margin: auto 0 0 0;
        width: max-content;
      }
      & img {
        margin-bottom: 1rem;
        height: 12rem;
        width: auto;
        border-radius: 0.25rem;
        &.big-img {
          height: 15rem;
          object-fit: cover;
          max-width: 100%;
          @include sm {
            height: auto;
            max-height: 15rem;
          }
        }
      }
      .item-description {
        opacity: 0.6;
        margin-bottom: 0.5rem;
      }
      @include lg {
        max-width: 250px;
      }
    }
  }
  .grid-layout-carousel {
    overflow: auto;
    grid-auto-flow: column;
  }
  @include min-md {
    .grid-layout-carousel {
      overflow: initial;
      grid-auto-flow: dense;
    }
  }
}
</style>
